import {
  RecorderUploaderError,
  type RecorderUploaderErrorCode,
  type RecorderUploaderResult,
  type RecorderUploadStateChangedPayload,
  type SessionId,
} from '@heidi/plugin-rpa';
import { logger } from '@heidi/runtime/logger';

/**
 * Maps uploader client results to upload-state payloads for the recordings atom.
 *
 * Includes `polling` so a missed Tauri emit after multipart success still moves the
 * row off "Uploading…". In-progress stages (`uploading`, `pending`, …) return null.
 */
export function uploaderResultToUploadStatePayload(
  sessionId: SessionId,
  result: RecorderUploaderResult
): RecorderUploadStateChangedPayload | null {
  if (!result.ok) {
    return {
      sessionId,
      stage: 'failed',
      uploadAttempts: 0,
      errorCode: result.error.code,
    };
  }

  const { state } = result;
  if (state.stage !== 'failed' && state.stage !== 'ready' && state.stage !== 'polling') {
    return null;
  }

  return {
    sessionId,
    stage: state.stage,
    uploadAttempts: state.uploadAttempts,
    recordingId: state.recordingId ?? undefined,
    recordingName: state.recordingName ?? undefined,
    failureKind: state.stage === 'failed' ? (state.failureKind ?? undefined) : undefined,
    errorCode:
      state.stage === 'failed'
        ? ((state.errorCode as RecorderUploaderErrorCode | null) ?? 'UNKNOWN')
        : undefined,
    errorMessage: state.errorMessage ?? undefined,
  };
}

export function applyUploaderResultToUi(
  sessionId: SessionId,
  result: RecorderUploaderResult,
  applyUploadState: (payload: RecorderUploadStateChangedPayload) => void
): void {
  const payload = uploaderResultToUploadStatePayload(sessionId, result);
  if (payload) {
    applyUploadState(payload);
  }
}

/** Applies the settled result of background uploader work, turning a throw into a failed row. */
export function settleUploaderWork(
  sessionId: SessionId,
  work: Promise<RecorderUploaderResult>,
  applyUploadState: (payload: RecorderUploadStateChangedPayload) => void,
  logContext: { readonly msg: string }
): void {
  void work.then(
    (result) => applyUploaderResultToUi(sessionId, result, applyUploadState),
    (error) => {
      logger.warn({
        msg: logContext.msg,
        sessionId,
        errorName: error instanceof Error ? error.name : typeof error,
        ...(error instanceof RecorderUploaderError ? { errorCode: error.code } : {}),
      });
      applyUploaderResultToUi(
        sessionId,
        {
          ok: false,
          error:
            error instanceof RecorderUploaderError
              ? error
              : new RecorderUploaderError('UNKNOWN', { cause: error }),
        },
        applyUploadState
      );
    }
  );
}
